import React, { useState } from 'react';
import { SparklesIcon, CheckIcon, XMarkIcon } from 'components/icons';
import { userApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

interface LearningFeedbackProps {
  inferredPrefs: {
    topNaicsCodes: { code: string; count: number; confidence: number }[];
    topAgencies: { name: string; count: number; confidence: number }[];
    topSetAsides: { code: string; count: number; confidence: number }[];
    interactionCount: number;
  } | null;
  onConfirmed?: () => void;
}

const DISMISS_KEY = 'learning_feedback_dismissed';
const DISMISS_DAYS = 14;

const LearningFeedback: React.FC<LearningFeedbackProps> = ({ inferredPrefs, onConfirmed }) => {
  const { refreshUser } = useAuth();
  const [visible, setVisible] = useState(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return false;
    }
    return true;
  });
  const [confirming, setConfirming] = useState(false);

  if (!visible || !inferredPrefs || inferredPrefs.interactionCount < 5) return null;

  // Pick the highest-confidence insight to show
  const topAgency = inferredPrefs.topAgencies?.find(a => a.confidence > 0.3);
  const topNaics = inferredPrefs.topNaicsCodes?.find(n => n.confidence > 0.3);
  const topSetAside = inferredPrefs.topSetAsides?.find(s => s.confidence > 0.3);

  if (!topAgency && !topNaics && !topSetAside) return null;

  // Pick the single best insight to show
  const insight = topAgency
    ? { type: 'agency', text: `${topAgency.name} contracts`, value: topAgency.name }
    : topNaics
    ? { type: 'naics', text: `NAICS ${topNaics.code} opportunities`, value: topNaics.code }
    : { type: 'setAside', text: `${topSetAside!.code} set-aside contracts`, value: topSetAside!.code };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const data: any = {};
      if (insight.type === 'agency') data.agencies = [insight.value];
      if (insight.type === 'naics') data.naicsCodes = [insight.value];
      if (insight.type === 'setAside') data.setAsides = [insight.value];

      await userApi.confirmPreferences(data);
      await refreshUser();
      toast.success('Preference confirmed');
      setVisible(false);
      onConfirmed?.();
    } catch {
      toast.error('Failed to save');
    } finally {
      setConfirming(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-lg">
      <SparklesIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
      <p className="text-sm text-content flex-1">
        Clevor noticed you're interested in <strong>{insight.text}</strong>
      </p>
      <button
        onClick={handleConfirm}
        disabled={confirming}
        className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-500 disabled:opacity-50 flex-shrink-0"
      >
        <CheckIcon className="h-3 w-3" />
        {confirming ? '...' : 'Confirm'}
      </button>
      <button onClick={handleDismiss} className="text-content-subtle hover:text-content-muted flex-shrink-0">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default LearningFeedback;
